import React from 'react';
import TrafficLandingTemplate, { LandingPageConfig, ResellerPricingConfig } from './TrafficLandingTemplate';
import { LayoutDashboard, Settings2, BarChart3 } from 'lucide-react';

const config: LandingPageConfig = {
    slug: 'reseller-program',
    titleTag: 'Traffic Reseller Program — Launch Your Own Traffic SaaS',
    metaDescription: 'Launch your own white-label traffic platform this week. Buy GA4-safe website traffic at wholesale and resell it under your brand. Full white-label website on your domain, your Stripe account, 100% of the margin.',
    keywords: 'traffic reseller program, white label traffic reseller, sell website traffic, traffic reseller business, resell web traffic, white label seo traffic, traffic arbitrage agency, website traffic wholesale, seo traffic reseller platform',
    primaryKeyword: 'traffic reseller program',
    secondaryKeyword: 'white label traffic reseller',
    reviewCount: '134',
    ratingValue: '4.8',

    heroPrimaryHref: 'https://wa.me/61489996250',
    heroSecondaryHref: '#pricing',
    ctaBottomPrimaryHref: 'https://wa.me/61489996250',
    ctaBottomSecondaryHref: '#pricing',

    h1: 'Launch Your Own Traffic Reseller Business',
    subheadline: 'Deploy a complete white-label traffic platform on your domain in 5–7 days. Your brand, your Stripe account, your prices — Traffic Creator runs the infrastructure invisibly in the background.',

    problemH2: 'You\'re Already in the Traffic Business — You\'re Just Not Getting Paid for It',
    problemText: 'Most SEO agencies buy traffic for clients already. They place manual orders, chase delivery confirmations, and explain GA4 reports by hand — then pass through the cost at retail and earn zero margin. The reseller model changes all of this: your clients log into your branded platform, configure their own campaigns, see their own GA4 sessions, and pay you directly via Stripe. You monitor everything from one admin dashboard without touching a single order manually.',
    solutionPoints: [
        'Your monthly plan fee is your traffic budget — pay €799/mo and receive €799 in wholesale traffic credits guaranteed every month',
        'At 45% off retail, €799 in wholesale credits = ~€1,453 in traffic value you can sell to clients at your own prices',
        'Clients log into traffic.youragency.com, configure campaigns, and see GA4 sessions — Traffic Creator stays invisible',
        'You keep 100% of what clients pay you above your wholesale cost — no revenue share, no per-order fees',
    ],

    featuresH2: 'What Your White-Label Platform Actually Includes',
    features: [
        {
            icon: <LayoutDashboard />,
            title: 'A Complete Client Dashboard at Your Domain',
            description: 'Each client gets a sub-account login at your branded domain. They see your logo, select traffic packages, configure campaign settings (target country, device type, daily volume, session length), and watch their delivery counter in real time. Every email they receive comes from your domain. They never see Traffic Creator\'s name.',
        },
        {
            icon: <Settings2 />,
            title: 'Campaign Configuration Clients Control Themselves',
            description: 'Clients don\'t just buy hits — they configure how traffic behaves. Target country from 190+ available. Desktop or mobile or mixed. Session duration from 30 seconds to 10 minutes. Pages per visit. Bounce rate target. Once a campaign is live, delivery starts within minutes and runs 24/7 at a natural distribution — not a sudden spike.',
        },
        {
            icon: <BarChart3 />,
            title: 'GA4 Sessions With Real Behavioral Signals',
            description: 'Traffic Creator uses a residential IP network that generates full behavioral sessions in GA4: each visit includes scroll events, click interactions, multi-page navigation, and a natural session duration. GA4 reports them as real sessions with city, device type, and traffic source. Clients can verify every delivery by opening their own Analytics account — no manual reporting required from you.',
        },
    ],

    testimonialsH2: 'What Reseller Partners Say',
    testimonials: [
        {
            name: 'James R.',
            role: 'SEO Agency Owner',
            result: '€1,800/mo New Margin',
            text: 'We were placing manual traffic orders for 8 clients and forwarding reports by hand. With the reseller platform, clients log in themselves, configure their campaigns, and see GA4 results directly. My admin view shows every campaign in one place. I went from spending 4 hours a week on traffic ops to zero — and now buy at wholesale instead of retail.',
            seed: 'jamesr',
        },
        {
            name: 'Petra M.',
            role: 'Digital Marketing Consultant',
            result: 'Own SaaS Live in 6 Days',
            text: 'Traffic Creator handled everything — DNS, SSL, deploying the platform at my domain, connecting Stripe, applying my logo and colors. On day 7 I sent my first clients a login link. They logged into traffic.myagency.com, saw my brand, and placed their first orders. The GA4 sessions showed up within an hour. Clients thought I had built the whole thing myself.',
            seed: 'petram',
        },
        {
            name: 'Daniel W.',
            role: 'Full-Service Agency Director',
            result: '15 Clients Onboarded in Month One',
            text: 'The sub-account system is exactly what we needed. I have a master admin view showing all 15 clients, their active campaigns, delivery status, and order history. Each client only sees their own account with our agency branding. Onboarding a new client takes about 5 minutes — I create the sub-account, send a login link, and they\'re self-sufficient from there.',
            seed: 'danielw',
        },
    ],

    ctaH2: 'Ready to Launch Your Traffic Reseller Business?',

    faqs: [
        {
            question: 'What does the setup process actually involve?',
            answer: 'You point your domain (e.g. traffic.youragency.com) to our servers — that\'s a one-line DNS change. We handle everything else: SSL certificate, platform deployment, branding (logo, colors, domain), and Stripe integration. Setup takes 5–7 business days. You receive an admin login when it\'s ready.',
        },
        {
            question: 'What campaign settings can clients configure on the platform?',
            answer: 'Clients select: target country (190+ available), device type (desktop / mobile / tablet / mixed), daily visitor volume, session duration (30 seconds to 10 minutes), pages per visit, and bounce rate target. Once configured and paid, the campaign goes live automatically. No manual intervention from you.',
        },
        {
            question: 'How does GA4 session visibility actually work?',
            answer: 'Traffic Creator uses residential IPs — real consumer internet connections, not datacenter proxies. Each session generates real behavioral signals: scroll events, click interactions, multi-page navigation, and time-on-page. GA4 sees these as organic-quality sessions and reports them with city-level location, device type, and session duration. Sessions typically appear in GA4 within 5–30 minutes of campaign start.',
        },
        {
            question: 'Will my clients know they\'re using Traffic Creator?',
            answer: 'No. Your platform runs on your domain with your branding. Order confirmations and delivery notifications come from your email domain. Your clients\' GA4 accounts show sessions — not any reference to the traffic source infrastructure. Traffic Creator is completely invisible in the client experience.',
        },
        {
            question: 'How do I manage multiple clients as a reseller?',
            answer: 'Your reseller admin panel shows all client sub-accounts, their active campaigns, delivery counters, and order history in one view. You can create new sub-accounts, adjust client settings, and see live delivery status across your entire book of clients. Each client only sees their own account.',
        },
        {
            question: 'Is the monthly plan fee just a platform fee, or does it include traffic?',
            answer: 'It includes traffic. Your monthly plan fee is your guaranteed wholesale traffic budget — not a separate platform charge on top of traffic costs. Pay €799/mo on the Starter plan and you receive €799 in wholesale traffic credits every month, guaranteed. At 45% below retail, that €799 buys you ~€1,453 in traffic value that you deliver to clients at whatever price you set. You keep everything above your wholesale cost.',
        },
    ],

    resellerPricing: {
        h2: 'Reseller Program',
        subtitle: 'Your monthly fee is your guaranteed traffic budget — not just a platform fee. Every euro you pay is credited as wholesale traffic you deliver to clients.',
        tiers: [
            {
                name: 'API Reseller',
                monthlyPrice: '€199',
                setupFee: 'No setup fee',
                discount: '35% off wholesale',
                subAccounts: '—',
                features: [
                    '€199/mo in guaranteed traffic credits',
                    '~€306 retail value to resell each month',
                    'Wholesale API access (REST, full docs)',
                    'Integrate into your existing client portal',
                    'All country targets and traffic types',
                    'Month-to-month, no commitment',
                ],
                ctaLabel: 'Start API Plan',
                ctaHref: 'https://wa.me/61489996250',
            },
            {
                name: 'White-Label Starter',
                monthlyPrice: '€799',
                setupFee: '€499 one-time setup',
                discount: '45% off wholesale',
                subAccounts: 'Up to 20 clients',
                highlighted: true,
                features: [
                    '€799/mo in guaranteed traffic credits',
                    '~€1,453 retail value to resell each month',
                    'Full branded platform on your domain',
                    'Your logo, brand colors, custom domain',
                    'Up to 20 client sub-accounts',
                    'Your Stripe account — 100% of revenue',
                    'Client campaign self-service dashboard',
                    'Reseller admin view across all clients',
                    'Setup in 5–7 business days',
                ],
                ctaLabel: 'Launch Starter',
                ctaHref: 'https://wa.me/61489996250',
            },
            {
                name: 'White-Label Pro',
                monthlyPrice: '€2,499',
                setupFee: '€999 one-time setup',
                discount: '55% off wholesale',
                subAccounts: 'Unlimited clients',
                features: [
                    '€2,499/mo in guaranteed traffic credits',
                    '~€5,553 retail value to resell each month',
                    'Everything in Starter',
                    'Unlimited client sub-accounts',
                    'Custom PDF delivery reports per client',
                    'Priority traffic delivery queue',
                    'Dedicated reseller support channel',
                    '3-month minimum term',
                ],
                ctaLabel: 'Launch Pro',
                ctaHref: 'https://wa.me/61489996250',
            },
            {
                name: 'Enterprise',
                monthlyPrice: 'Custom',
                setupFee: 'Custom setup',
                discount: '60%+ off wholesale',
                subAccounts: 'Unlimited',
                features: [
                    'Custom monthly traffic credit budget',
                    '60%+ wholesale discount',
                    'Everything in Pro',
                    'Dedicated account manager',
                    'Custom SLA and uptime guarantee',
                    'Multi-region traffic routing options',
                    'Co-branded client onboarding',
                ],
                ctaLabel: 'Contact Us',
                ctaHref: 'https://wa.me/61489996250',
            },
        ],
    } as ResellerPricingConfig,

    relatedLinks: [
        { label: 'Start a Reseller Business', to: '/start-traffic-reseller-business' },
        { label: 'Compare Reseller Programs', to: '/traffic-reseller-program-comparison' },
        { label: 'White Label Traffic API', to: '/white-label-traffic-api' },
    ],

    translations: {
        heroCtaPrimary: 'Book a Reseller Demo',
        heroCtaSecondary: 'View Plans & Pricing',
        heroMicrocopy: 'Setup in 5–7 days. No revenue share. 100% white-label.',
        ctaButtonPrimary: 'Book a Demo on WhatsApp',
        ctaButtonSecondary: 'See the Plans',
        ctaSubtitle: 'Launch your own traffic SaaS this week. Your brand, your Stripe account, your margin.',
        ctaMicrocopy: '€499 one-time setup. Month-to-month billing. Cancel anytime.',
        featuresSubtitle: 'Everything your white-label reseller platform includes from day one.',
        howItWorksH2: '3 Steps',
        howItWorksSteps: [
            { title: 'Deploy Your Platform', desc: 'Traffic Creator configures your branded SaaS at your domain — DNS, SSL, Stripe, your logo and colors. Done in 5–7 days without you writing a single line of code.' },
            { title: 'Onboard Your Clients', desc: 'Create a sub-account and send a login link. Clients configure their own campaigns — country, device, daily volume, session length — and see GA4 sessions within an hour.' },
            { title: 'Scale Your Income', desc: 'Your admin panel shows all clients and their delivery in one view. Adding a new client takes 5 minutes. Every client above break-even is pure margin with no extra platform cost.' },
        ],
        problemBullets: [
            'Buying traffic for clients at retail — zero margin',
            'Manual order processing eating agency time every week',
            'No branded platform — clients see competitor dashboards',
            'No recurring revenue model for traffic services',
        ],
    },

    productName: 'Traffic Creator Reseller Program',
    productDescription: 'White-label traffic reseller program. Full branded SaaS platform on your domain, client campaign self-service, GA4 behavioral sessions, your Stripe, your margin. Setup in 5–7 days.',
};

const ResellerProgram: React.FC = () => {
    return <TrafficLandingTemplate config={config} />;
};

export default ResellerProgram;
